import * as React from "react";
import { cn } from "../lib";

interface FlyinProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  className?: string;
  overlayClassName?: string;
  contentClassName?: string;
}

const Flyin = ({
  open,
  onOpenChange,
  children,
  className,
  overlayClassName,
  contentClassName,
}: FlyinProps) => {
  React.useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onOpenChange(false);
      }
    };

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onOpenChange]);

  if (!open) return null;

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 flex items-stretch justify-end",
        className,
      )}
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Close panel"
        onClick={() => onOpenChange(false)}
        className={cn("absolute inset-0 bg-black/40", overlayClassName)}
      />
      <div
        className={cn(
          "relative flex h-full w-full max-w-[520px] rounded-l-[16px] flex-col overflow-hidden bg-white shadow-[0_24px_60px_rgba(15,20,40,0.25)]",
          contentClassName,
        )}
      >
        {children}
      </div>
    </div>
  );
};

const FlyinHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex items-start justify-between px-6 pt-6", className)}
    {...props}
  />
);

const FlyinTitle = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h2
    className={cn("text-[22px] font-[600] text-[#161827]", className)}
    {...props}
  >
    {children ?? <span className="sr-only">Fly-in title</span>}
  </h2>
);

const FlyinBody = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex-1 overflow-y-auto px-6 py-6", className)}
    {...props}
  />
);

const FlyinFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex items-center gap-6 border-[#E6E9F5] px-6 py-5",
      className,
    )}
    {...props}
  />
);

export { Flyin, FlyinHeader, FlyinTitle, FlyinBody, FlyinFooter };
